import { memo, ReactNode } from 'react'

import { Box, FormControlLabel, Switch, TextField, Typography } from '@mui/material'
import { format } from 'date-fns'
import { Controller, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import type { ConfigFormValues } from './types'

export type ScheduleFieldsProps = {
  /** Form actions (e.g. Cancel/Reset/Save) rendered inline in the "Schedule" header row. */
  actions?: ReactNode
}

const LOCAL_FORMAT = "yyyy-MM-dd'T'HH:mm"
const toInput = (iso?: string | null) => (iso ? format(new Date(iso), LOCAL_FORMAT) : '')
const fromInput = (value: string) => (value ? new Date(value).toISOString() : '')

/** Start/end schedule editor bound to `manualStartTime`/`manualEndTime` on the shared config form. */
const ScheduleFieldsRaw = ({ actions }: ScheduleFieldsProps) => {
  const { t } = useTranslation(['common', 'domain'])

  const { control, watch, setValue, getValues, trigger } = useFormContext<ConfigFormValues>()
  const openEnd = watch('manualEndTime') == null

  return (
    <Box>
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Typography variant="heading-md">{t('domain:Workspace.Event.schedule')}</Typography>
        {actions && <Box sx={{ display: 'flex', gap: 1 }}>{actions}</Box>}
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: 2 }}>
        <Controller
          control={control}
          name="manualStartTime"
          render={({ field }) => (
            <TextField
              type="datetime-local"
              label={t('domain:Workspace.Event.startTime')}
              value={toInput(field.value)}
              onChange={(e) => {
                field.onChange(fromInput(e.target.value))
                // Re-validate the end field so the "before start" error clears/appears live.
                void trigger('manualEndTime')
              }}
              InputLabelProps={{ shrink: true }}
            />
          )}
        />
        <Controller
          control={control}
          name="manualEndTime"
          rules={{
            validate: (value) => {
              const start = getValues('manualStartTime')
              if (value == null || !start) {
                return true
              }
              return (
                new Date(value).getTime() >= new Date(start).getTime() ||
                t('domain:Workspace.Event.endBeforeStart')
              )
            },
          }}
          render={({ field, fieldState }) => (
            <TextField
              // While "open end" is on, drop the datetime-local input so its greyed
              // "dd.mm.yyyy, --:--" placeholder disappears — show an empty text field instead.
              type={openEnd ? 'text' : 'datetime-local'}
              label={t('domain:Workspace.Event.endTime')}
              value={openEnd ? '' : toInput(field.value)}
              disabled={openEnd}
              onChange={(e) => field.onChange(fromInput(e.target.value))}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              InputLabelProps={{ shrink: true }}
            />
          )}
        />
        <FormControlLabel
          sx={{ mt: 1 }}
          control={
            <Switch
              checked={openEnd}
              onChange={(e) =>
                setValue('manualEndTime', e.target.checked ? null : (watch('manualStartTime') ?? ''), {
                  shouldDirty: true,
                })
              }
            />
          }
          label={t('domain:Workspace.Event.openEnd')}
        />
      </Box>
    </Box>
  )
}

export const ScheduleFields = memo(ScheduleFieldsRaw)
